import { css } from "@emotion/react";
import Image from "next/image";
import Link from "next/link";
import { FaExternalLinkAlt } from "react-icons/fa";

import {
  fixedHeight,
  fixedWidth,
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
} from "~/lib/client/design";
import useIsSpMode, { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import {
  MeQuestTaskContentQuery,
  UserQuestTaskProgressStatus,
} from "~/lib/graphql/client";
import { formatDateTime } from "~/lib/graphql/graphqlClient";

const DialogBody = ({ content }: { content: MeQuestTaskContentQuery }) => {
  const items = content.me.userQuestTaskProgresses
    .filter((p) => p.status === UserQuestTaskProgressStatus.Verified)
    .map((p) =>
      p.media.map((m) => ({
        id: m.id,
        description: p.questTask.title,
        url: m.url,
        createdAt: formatDateTime(p.createdAt),
        questUrl: "/quest/" + p.questTask.quest.id,
      }))
    )
    .flat();
  return (
    <div>
      <h2>Quest Memory</h2>
      {items.length === 0 ? (
        <p>
          <span>クエストを実行した記録がありません</span>
          <br />
          <span>
            あなたのFaveを探して応援しましょう！
            <Link
              href={"/category"}
              css={css`
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
              `}
            >
              Fave一覧
              <FaExternalLinkAlt />
            </Link>
          </span>
        </p>
      ) : (
        <div css={itemContainerStyle}>
          {items.map((item) => (
            <TaskItemPanel key={item.id} {...item} />
          ))}
        </div>
      )}
    </div>
  );
};

const TaskItemPanel = ({
  description,
  url,
  createdAt,
  questUrl,
}: {
  description: string;
  url: string;
  createdAt: string;
  questUrl: string;
}) => {
  const isSpMode = useIsSpMode();

  if (typeof isSpMode === "undefined") return null;

  return (
    <Link css={itemRootStyle(isSpMode)} href={questUrl}>
      <Image
        src={url}
        alt={description + "の画像"}
        width={isSpMode ? 160 : 230}
        height={isSpMode ? 120 : 172}
        loading="lazy"
        css={taskImageStyle}
      />
      <p css={descriptionStyle}>{description}</p>
      <p css={createdAtStyle}>{createdAt}</p>
    </Link>
  );
};

const itemContainerStyle = css`
  display: grid;
  margin: 1rem auto 0;

  ${mediaQueryIsMobile(css`
    width: calc(100% + 2rem);
    gap: 0.5rem;
    margin-left: -1rem;
    margin-right: -1rem;
    justify-content: center;
    grid-template-columns: repeat(auto-fill, 160px);
  `)};

  ${mediaQueryIsDesktop(css`
    width: 100%;
    justify-content: center;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, 230px);
  `)};
`;

const itemRootStyle = (isSpMode: boolean) => css`
  ${fixedWidth(isSpMode ? "160px" : "230px")};
  ${fixedHeight(isSpMode ? "160px" : "230px")};
  border-radius: 20px;
  border: 1px solid #fff;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
`;

const taskImageStyle = css`
  border-radius: 20px 20px 0 0;
  width: 100%;
  object-fit: contain;
  padding: 0.25rem;
`;

const descriptionStyle = css`
  font-size: 10px;
  font-weight: 700;
  margin: 0.5rem 0 0 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    margin: 0.25rem 0 0 1rem;
  }
`;

const createdAtStyle = css`
  font-size: 10px;
  margin: 0.25rem 0 0 0.5rem;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    margin: 0.25rem 0 0 1rem;
  }
`;

export default DialogBody;
